import { hasOnlyPermission, transformPermissions } from 'common/features/userPermissions/util';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getUser } from 'redux/slices/authSlice';
import { getLicense } from 'redux/slices/licenseSlice';

import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import { useSettingValue } from 'common/hooks/useSettingValue';

import { PERMISSIONS } from 'common/features/userPermissions/PermissionsConst';
import { UserPermissions } from 'common/features/userPermissions/PermissionsEnum';

export function useDefaultServiceRoute() {
    const license = useSelector(getLicense);
    const user = useSelector(getUser);
    const location = useLocation();

    const userPermissions = useMemo(() => transformPermissions(user?.permissions), [user?.permissions]);

    const calendarVersion = useSettingValue(SettingsKey.CALENDAR_VERSION) ?? 3;

    const isRoot = user?.role === 'root';
    const isOrgAdmin = !user?.userGroupId;
    const hasEmptyPermissions = Array.isArray(user?.permissions) && user.permissions.length === 0;

    const search = location.search;
    const currentPath = location.pathname;

    const hasPermission = useCallback(
        (resource, strict = false) => {
            const shouldBypassPermissions = strict ? isRoot : isRoot || (isOrgAdmin && hasEmptyPermissions);

            if (shouldBypassPermissions) return true;
            if (!userPermissions) return true;

            return userPermissions[resource]?.actions?.length > 0;
        },
        [userPermissions, isRoot, isOrgAdmin, hasEmptyPermissions],
    );

    const getCalendarRoute = useCallback(() => {
        return Number(calendarVersion) === 4 ? '/v4-i-calendar' : '/i-calendar';
    }, [calendarVersion]);

    const getServicesRoute = useCallback(() => {
        return Number(calendarVersion) === 4 ? '/v4-services' : '/services';
    }, [calendarVersion]);

    const onlyHasQrConfirmation = useMemo(() => hasOnlyPermission(user, UserPermissions.QR_CONFIRMATION), [user]);

    const isScopedUser = useMemo(() => {
        if (isRoot || (isOrgAdmin && hasEmptyPermissions)) return false;
        if (!userPermissions) return false;
        return Object.values(userPermissions).some((value) => value?.actions?.length > 0);
    }, [userPermissions, isRoot, isOrgAdmin, hasEmptyPermissions]);

    const resolveRoute = useCallback(
        (pathname) => {
            if (!license) return '/my-profile';

            switch (pathname) {
                case '/one-to-many':
                    return license?.services?.oneToMany?.iLive ? '/one-to-many' : '/my-profile';

                case '/one-to-one':
                    return license?.services?.oneToOne?.iCall ? '/one-to-one' : '/my-profile';

                case '/i-calendar':
                case '/v4-i-calendar':
                    return license?.services?.oneToOne?.iCalendar ? getCalendarRoute() : '/my-profile';

                case '/i-chat':
                    return license?.services?.oneToOne?.iChat ? '/i-chat' : '/my-profile';

                case '/shoppable':
                    return license?.services?.oneToMany?.shoppable ? '/shoppable' : '/my-profile';

                case '/services':
                case '/v4-services':
                    return license?.services?.oneToOne?.iCalendar && hasPermission(UserPermissions.SERVICE, false)
                        ? getServicesRoute()
                        : '/my-profile';

                case '/i-events':
                    return license?.services?.oneToOne?.iEvents && hasPermission(UserPermissions.IEVENTS, true)
                        ? '/i-events'
                        : '/my-profile';

                case '/i-store':
                    return license?.services?.oneToOne?.iStore && hasPermission(UserPermissions.ISTORE, true)
                        ? '/i-store'
                        : '/my-profile';

                case '/leads':
                    return license?.services?.leads && hasPermission(UserPermissions.LEAD, false)
                        ? '/leads'
                        : '/my-profile';

                case '/qr-confirmation':
                    return hasPermission(UserPermissions.QR_CONFIRMATION, true) ? '/qr-confirmation' : '/my-profile';

                default:
                    return '/my-profile';
            }
        },
        [license, getCalendarRoute, hasPermission, getServicesRoute],
    );

    const getDefaultLanding = useCallback(() => {
        // Scoped users land on the first accessible route granted by their permissions,
        // not on license-based defaults they may have no access to
        if (isScopedUser) {
            for (const { resource, route } of PERMISSIONS) {
                if (userPermissions?.[resource]?.actions?.length > 0) {
                    const resolved = resolveRoute(route);
                    if (resolved !== '/my-profile') return resolved;
                }
            }
            return '/my-profile';
        }
        if (license?.services?.oneToOne?.iCalendar) {
            return getCalendarRoute();
        }
        if (license?.services?.oneToMany?.iLive) {
            return '/one-to-many';
        }
        if (license?.services?.oneToOne?.iCall) {
            return '/one-to-one';
        }
        if (license?.services?.oneToOne?.iChat) {
            return '/i-chat';
        }
        if (license?.services?.oneToMany?.shoppable) {
            return '/shoppable';
        }
    }, [isScopedUser, userPermissions, resolveRoute, license, getCalendarRoute]);

    // Users whose only permission is qrConfirmation must always land on its route
    if (onlyHasQrConfirmation) {
        return `/qr-confirmation${search}`;
    }

    if (currentPath === '/loading') {
        return `${getDefaultLanding()}${search}`;
    }

    const resolvedRoute = resolveRoute(currentPath);

    return `${resolvedRoute}${search}`;
}
